'use client';

import { FC, useState, useMemo } from 'react';
import { useClientTranslation } from '@bts-web/utils-lokalise';
import { getFATCATranslations } from '../utils/getFATCATranslations';
import { TFunction } from 'i18next';
import { FATCA_PAGES_KEYS } from '../constants';
import { ConfirmDetailsScreen } from './subComponents/ConfirmDetailsScreen';
import { useFATCAFormState } from '../utils/useFATCAFormState';
import { TransfersLayout } from '../../crypto-transfers/components/Reusable/TransfersLayout/TransfersLayout';
import { FATCAModalActionBar } from './subComponents/FATCAModalActionBar/FATCAModalActionBar';
import { SavingsPlanNavigationBarSkeleton } from '../../savings-plans/savings-plan-details/SavingsPlanDetailsSkeleton/SavingsPlanDetailsSkeleton';
import { AssetsListSkeleton } from '../../prices/AssetsDiscoverPageSkeleton/AssetsDiscoverPageSkeleton';
import { css } from '@bts-web/utils-style-engine';
import { QuestionnaireOrFeatureTitleAndDescription } from '../../common/components/QuestionnaireOrFeatureTitleAndDescription/QuestionnaireOrFeatureTitleAndDescription';
import { FATCATaxationOptionsScreen } from './subComponents/FATCATaxationOptionsScreen/FATCATaxationOptionsScreen';
import { FATCARequireCertificationScreen } from './subComponents/FATCARequireCertificationScreen/FATCARequireCertificationScreen';
import { FATCANoAccessRealStocksScreen } from './subComponents/FATCANoAccessRealStocksScreen/FATCANoAccessRealStocksScreen';
import { FATCATaxResidencyPrimaryHub } from './subComponents/FATCATaxResidencyPrimaryHub/FATCATaxResidencyPrimaryHub';
import { FATCATinMissingReasonPage } from './subComponents/FATCATinMissingReasonPage/FATCATinMissingReasonPage';
import { FATCAResidencyJustifyNoTinNumberInTextScreen } from './subComponents/FATCAResidencyJustifyNoTinNumberInTextScreen/FATCAResidencyJustifyNoTinNumberInTextScreen';

interface FATCAControllerProps {
  onClose?: () => void;
}

const FATCAController: FC<FATCAControllerProps> = ({ onClose }) => {
  const { t } = useClientTranslation();

  const translations = getFATCATranslations(t as TFunction);

  const [currentPage, setCurrentPage] = useState<FATCA_PAGES_KEYS>(
    FATCA_PAGES_KEYS.FATCA_USER_DATA,
  );

  const fatcaFormState = useFATCAFormState({
    setCurrentPage,
    currentPage,
    translations,
    onFATCAModalClose: onClose,
  });

  const { loading, stepsInformation, sectionTitleAndDescription } =
    fatcaFormState;

  const { title, description, subtitle } = sectionTitleAndDescription ?? {};

  const pageComponents = useMemo(
    () => ({
      [FATCA_PAGES_KEYS.FATCA_USER_DATA]: ConfirmDetailsScreen,
      [FATCA_PAGES_KEYS.FATCA_TAXATION_OPTIONS]: FATCATaxationOptionsScreen,
      [FATCA_PAGES_KEYS.FATCA_FINAL_ALLOWED]: FATCARequireCertificationScreen,
      [FATCA_PAGES_KEYS.FATCA_FINAL_DISALLOWED]: FATCANoAccessRealStocksScreen,
      [FATCA_PAGES_KEYS.FATCA_TAX_RESIDENCY_PRIMARY_HUB]:
        FATCATaxResidencyPrimaryHub,
      [FATCA_PAGES_KEYS.FATCA_MISSING_TIN_REASON_PAGE]:
        FATCATinMissingReasonPage,
      [FATCA_PAGES_KEYS.FATCA_MISSING_TIN_TEXT_REASON_PAGE]:
        FATCAResidencyJustifyNoTinNumberInTextScreen,
    }),
    [],
  );

  const CurrentPageComponent = pageComponents[currentPage];

  const renderTopRegion = () => {
    if (loading) {
      return (
        <div className={css({ py: 'small' })}>
          <SavingsPlanNavigationBarSkeleton />
        </div>
      );
    }

    return (
      <>
        <FATCAModalActionBar
          currentPage={currentPage}
          setPage={setCurrentPage}
          translations={translations}
          onClose={onClose}
          stepInformation={stepsInformation}
        />
        {(title || description || subtitle) && (
          <QuestionnaireOrFeatureTitleAndDescription
            title={title}
            subtitle={subtitle}
            description={description}
          />
        )}
      </>
    );
  };

  const renderMiddleRegion = () => {
    if (loading) return <AssetsListSkeleton />;

    if (!CurrentPageComponent) return null;

    return (
      <CurrentPageComponent
        fatcaFormState={fatcaFormState}
        translations={translations}
      />
    );
  };

  return (
    <TransfersLayout
      wrapperProps={{
        className: css({ textAlign: 'start' }),
      }}
      topRegion={renderTopRegion()}
      middleScrollableRegion={renderMiddleRegion()}
    />
  );
};

export { FATCAController };
